import {Injectable} from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpHeaderResponse,
  HttpInterceptor,
  HttpProgressEvent,
  HttpRequest,
  HttpResponse,
  HttpSentEvent,
  HttpUserEvent
} from '@angular/common/http';
import {BehaviorSubject, Observable, throwError} from 'rxjs';

import {environment} from '../../environments/environment';
import {catchError, filter, switchMap, take} from 'rxjs/operators';
import {ApiHttpResponse} from '../shared/models/api-http-response.interface';
import {AuthService} from '../core/services/auth.service';
import {Router} from '@angular/router';
import {StorageService} from '../core/services/storage.service';
import {RefreshToken} from '../core/models/refresh-token.interface';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  constructor(private authService: AuthService,
              private storageService: StorageService,
              private router: Router
  ) {
  }

  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);


  static isLoginRequest(request: HttpRequest<any>): boolean {
    return request.url.includes('refresh-token') || request.url.includes('login');
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const isApiUrl = request.url.startsWith(environment.apiUrl);
    if (isApiUrl && !AuthInterceptor.isLoginRequest(request)) {
      // add auth header with jwt if user is logged in and request is to the api url
      if (this.authService.isLoggedIn()) {
        request = this.addHeaders(request, this.storageService.getToken());
      }

      return next.handle(request)
        .pipe(catchError(error => {
          if (error instanceof HttpErrorResponse && error.status === 401) {
            return this.handle401Error(request, next);
          } else {
            return throwError(error);
          }
        }));

    }

    return next.handle(request);
  }

  private addHeaders(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`
      }
    });
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler):
    Observable<HttpSentEvent | HttpHeaderResponse | HttpResponse<any> | HttpProgressEvent | HttpUserEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this.authService.refreshToken().pipe(
        switchMap((refreshToken: ApiHttpResponse<RefreshToken>) => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(refreshToken.content.token);

          return next.handle(this.addHeaders(request, refreshToken.content.token));
        }),
        catchError((err) => {
          this.isRefreshing = false;
          this.authService.logout();
          this.router.navigateByUrl('/login')
            .catch(error => throwError(error));
          return throwError(err);
        })
      );
    }
    return this.refreshTokenSubject.pipe(
      filter(token => token !== null),
      take(1),
      switchMap((jwt: string) => next.handle(this.addHeaders(request, jwt)))
    );
  }
}
